
// Ramer-Douglas-Peucker Simplification
const getSqDist = (p1, p2) => (p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2;
const getSqSegDist = (p, p1, p2) => {
    let x = p1.x, y = p1.y, dx = p2.x - x, dy = p2.y - y;
    if (dx !== 0 || dy !== 0) {
        const t = ((p.x - x) * dx + (p.y - y) * dy) / (dx * dx + dy * dy);
        if (t > 1) { x = p2.x; y = p2.y; }
        else if (t > 0) { x += dx * t; y += dy * t; }
    }
    return (p.x - x) ** 2 + (p.y - y) ** 2;
};

const simplifyDPStep = (points, first, last, sqTolerance, simplified) => {
    let maxSqDist = sqTolerance, index = -1;
    for (let i = first + 1; i < last; i++) {
        const sqDist = getSqSegDist(points[i], points[first], points[last]);
        if (sqDist > maxSqDist) { index = i; maxSqDist = sqDist; }
    }
    if (index > -1) {
        if (index - first > 1) simplifyDPStep(points, first, index, sqTolerance, simplified);
        simplified.push(points[index]);
        if (last - index > 1) simplifyDPStep(points, index, last, sqTolerance, simplified);
    }
};

export const simplifyRoute = (points, tolerance) => {
    if (!points || points.length <= 2) return points;
    const sqTolerance = tolerance * tolerance;
    let result = [points[0]];
    simplifyDPStep(points, 0, points.length - 1, sqTolerance, result);
    result.push(points[points.length - 1]);
    return result;
};

export const smoothRoute = (points, iterations) => {
    if (!points || points.length < 3) return points;
    let currentPts = [...points];

    for (let iter = 0; iter < iterations; iter++) {
        const nextPts = [currentPts[0]]; // Start fixed
        for (let i = 1; i < currentPts.length - 1; i++) {
            const prev = currentPts[i - 1];
            const curr = currentPts[i];
            const next = currentPts[i + 1];
            nextPts.push({
                x: 0.25 * prev.x + 0.5 * curr.x + 0.25 * next.x,
                y: 0.25 * prev.y + 0.5 * curr.y + 0.25 * next.y
            });
        }
        nextPts.push(currentPts[currentPts.length - 1]); // End fixed
        currentPts = nextPts;
    }
    return currentPts;
};

// Main Processor
export const processRoute = (route, smoothLvl, straightLvl) => {
    if (route.isPreset) return route;

    // Source of Truth is pointsRaw. 
    // If not present, fallback to pointsAbs (but careful if that's already processed).
    // Ideally user sets pointsRaw = pointsAbs on first process.
    const raw = route.pointsRaw || route.pointsAbs || route.points;

    // If we don't have raw, we can't process destructively/non-destructively safely?
    // We'll treat 'raw' as the base.
    if (!raw || raw.length < 2) return route;

    let processed = raw;

    // 1. Straighten (Simplification)
    if (straightLvl > 0) {
        // Map 1-10 to 0.5 - 5.0 tolerance?
        // Tolerance 1.2 was nice.
        // straightLvl 5 -> 1.5
        // straightLvl 1 -> 0.3
        const tol = straightLvl * 0.3;
        processed = simplifyRoute(processed, tol);
    }

    // 2. Smooth
    if (smoothLvl > 0) {
        processed = smoothRoute(processed, smoothLvl);
    }

    return {
        ...route,
        pointsRaw: raw, // Ensure we keep the raw
        pointsAbs: processed
    };
};
